import { DOCUMENT } from '@angular/common'
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core'
import { NoticeSession } from '@models/planning/notice-session.model'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { NoticeSessionChatFloatingService } from './notice-session-chat-floating.service'

/**
 * Chat da sessão num balão (27/09/2026): botão flutuante no canto inferior direito da tela do agente
 * (`bid-board`), que abre o chat ancorado nele. Substitui a aba "Chat" — o agente conversa sem sair da
 * aba em que está. Desde 29/09/2026 fica em todas as abas, inclusive a sub-aba Lances, que só traz o chat
 * embutido com o painel maximizado (aí o botão sai). Fechado, o chat continua montado e o botão mostra
 * quantas mensagens chegaram.
 *
 * O elemento vai para o `body`. O formulário do processo fica num contêiner animado que termina com
 * `transform` aplicado (animate.css com `fill-mode: both`), e `position: fixed` dentro dele contaria a
 * partir do contêiner, não da janela — o mesmo motivo do `container="body"` dos dropdowns.
 */
@Component({
	selector: 'app-notice-session-chat-launcher',
	templateUrl: './notice-session-chat-launcher.component.html',
	styleUrls: ['./notice-session-chat-launcher.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class NoticeSessionChatLauncherComponent implements OnInit, OnChanges, OnDestroy {
	@Input() noticeSession!: NoticeSession
	/** Balão aberto — controlado pelo pai: aberto ao entrar com a sessão aberta e quando o agente a abre (29/09/2026). */
	@Input() open = false
	@Output() openChange = new EventEmitter<boolean>()

	/** Mensagens que entraram com o balão fechado — o badge do botão; abrir zera. */
	unreadCount = 0

	/**
	 * Sessão do chat flutuante global ("destacar" do chat dos Lances maximizados, ver `NoticeSessionChatFloatingHostComponent`).
	 * Ele continua na tela ao sair de Lances e ocupa o mesmo canto: enquanto estiver aberto para esta
	 * sessão, o botão some — um chat por vez.
	 */
	private floatingSessionId: string | null = null
	private readonly destroy$ = new Subject<void>()

	constructor(
		private host: ElementRef<HTMLElement>,
		private cdr: ChangeDetectorRef,
		private floatingService: NoticeSessionChatFloatingService,
		@Inject(DOCUMENT) private document: Document
	) {}

	ngOnInit(): void {
		this.document.body.appendChild(this.host.nativeElement)

		this.floatingService.state$.pipe(takeUntil(this.destroy$)).subscribe(state => {
			this.floatingSessionId = state?.noticeSession?.id ?? null

			// Fora do ciclo em que o estado chegou: na primeira emissão (síncrona, durante o `ngOnInit`)
			// avisar o pai no mesmo ciclo mudaria um binding já verificado.
			if (this.hiddenByFloating && this.open) {
				void Promise.resolve().then(() => this.setOpen(false))
			}

			this.cdr.markForCheck()
		})
	}

	ngOnChanges(changes: SimpleChanges): void {
		// Aberto pelo pai (atalho "Chat", entrada no processo): o que chegou já está à vista.
		if (changes['open'] && this.open) {
			this.unreadCount = 0
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next()
		this.destroy$.complete()
		// O Angular remove o host com `element.remove()`, que independe de onde ele está; a remoção
		// explícita garante que nada fique no `body` se a ordem de destruição mudar.
		this.host.nativeElement.remove()
	}

	get hiddenByFloating(): boolean {
		return !!this.floatingSessionId && this.floatingSessionId === this.noticeSession?.id
	}

	/** Texto do badge: acima de 99 não cabe no botão. */
	get unreadLabel(): string {
		return this.unreadCount > 99 ? '99+' : String(this.unreadCount)
	}

	get buttonLabel(): string {
		if (this.open) return 'Fechar o chat da sessão'
		if (!this.unreadCount) return 'Abrir o chat da sessão'

		return `Abrir o chat da sessão — ${this.unreadLabel} ${this.unreadCount === 1 ? 'mensagem nova' : 'mensagens novas'}`
	}

	/** Só conta com o balão fechado: aberto, a mensagem já está na tela. */
	onMessageReceived(): void {
		if (this.open) return

		this.unreadCount++
		this.cdr.markForCheck()
	}

	toggle(): void {
		this.setOpen(!this.open)
	}

	close(): void {
		this.setOpen(false)
	}

	private setOpen(open: boolean): void {
		if (this.open === open) return

		this.open = open
		if (open) this.unreadCount = 0
		this.openChange.emit(open)
		this.cdr.markForCheck()
	}
}
